import React, { useLayoutEffect, useRef, useState } from 'react';

// The reference's Waitlist tab is replaced by Cancelled: CampsEz has no waitlist. Cancelled holds
// cancelled participants, refunded or not (utils/registrations isCancelledRow).
const tabs = [
  { key: 'all', label: 'All sign-ups', tone: 'neutral' },
  { key: 'attention', label: 'Needs attention', tone: 'warning' },
  { key: 'medical', label: 'Medical & allergies', tone: 'neutral' },
  { key: 'cancelled', label: 'Cancelled', tone: 'neutral' },
];

/*
 * One underline for the bar, moved under the active tab with a CSS transition (the same 220 ms
 * ease the reference used). Not framer-motion's layoutId: shared layout measures the page
 * (scrollLeft) on every re-render of the page, which forces a full layout each time. This measures
 * the active tab only when it changes or resizes (counts, fonts, window width).
 */
export function RegistrationTabs({ active, counts, onChange }) {
  const tabRefs = useRef({});
  const [bar, setBar] = useState(null);
  // No slide on the first placement, so the line doesn't sweep in from the left on load.
  const placed = useRef(false);

  useLayoutEffect(() => {
    const el = tabRefs.current[active];
    if (!el) return undefined;
    const measure = () => setBar({ left: el.offsetLeft, width: el.offsetWidth });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);

  useLayoutEffect(() => {
    if (bar) placed.current = true;
  }, [bar]);

  return (
    <div role="tablist" aria-label="Registration views" className="scrollbar-thin tw-relative tw-flex tw-gap-6 tw-overflow-x-auto tw-border-b tw-border-neutral-200 tw-px-5">
      {tabs.map(t => {
        const selected = t.key === active;
        const countClass = selected
          ? 'tw-bg-brand-600 tw-text-white'
          : t.tone === 'warning' && counts[t.key] > 0
            ? 'tw-bg-amber-50 tw-text-amber-700'
            : 'tw-bg-sand-50 tw-text-neutral-500';
        return (
          <button
            key={t.key}
            ref={el => {
              tabRefs.current[t.key] = el;
            }}
            role="tab"
            type="button"
            aria-selected={selected}
            onClick={() => onChange(t.key)}
            className={`tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-whitespace-nowrap tw-py-3.5 tw-text-sm tw-font-medium tw-transition-colors tw-duration-150 ${selected ? 'tw-text-neutral-900' : 'tw-text-neutral-500 hover:tw-text-neutral-900'}`}>
            {t.label}
            <span className={`tw-rounded-full tw-px-1.5 tw-py-px tw-text-[11px] tw-font-semibold tw-tabular-nums ${countClass}`}>{counts[t.key] ?? 0}</span>
          </button>
        );
      })}
      {bar && (
        <span
          aria-hidden
          className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-h-0.5 tw-rounded-full tw-bg-brand-600"
          style={{
            width: bar.width,
            transform: `translateX(${bar.left}px)`,
            transition: placed.current ? 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1), width 220ms cubic-bezier(0.23, 1, 0.32, 1)' : 'none',
          }}
        />
      )}
    </div>
  );
}
